// queueHealth — V3 słaby zasięg B-1, UZUPEŁNIENIE 2 (12.09.2026, decyzja Martina):
// „baner kolejki TYLKO na wyjątki + stopka". Zasada kokpitu: lampki tylko od tego, co NIE działa.
//
// Przy queue-first kolejka jest stanem normalnym, nie wyjątkiem: schodząca kolejka = cisza
// na ekranie (⏳ w slotach zostaje). Baner kolejki pojawia się WYŁĄCZNIE gdy:
//   (a) ZATOR:  jest zasięg (online, nie „słaby zasięg" z B3), kolejka niepusta, a od 5 min
//               żadna wysyłka nie zakończyła się sukcesem — liczone od ostatniego SUKCESU
//               (nie od ostatniej próby), a przy braku sukcesu od najstarszego wpisu w kolejce;
//   (b) FAILED: wpis po 5 nieudanych próbach (status 'failed');
//   (c) PRÓG:   kolejka > 30 zdjęć LUB > 100 MB (informacyjny).
// Baner znika sam, gdy warunek ustaje. Ostrzeżenie > 500 MB (B12f) zostaje osobnym paskiem.
//
// STOPKA: jedna cienka linia na dole każdego ekranu po zalogowaniu, bez interakcji:
//   „⏳ 7 zdjęć czeka · ostatnie wysłane 40 s temu" / „✓ wszystko wysłane · ostatnie 3 min temu"
//   / „🔒 Tryb lokalny · 7 zdjęć na telefonie"; czas względny odświeżany co 30 s.
//
// Ostatni SUKCES wysyłki trzymany w IDB (store `settings`, klucz `last_upload_success_at`):
// przeżywa restart aplikacji i wylogowanie (stopka po ponownym otwarciu nadal wie, kiedy
// ostatnio coś poszło). Czyste funkcje (evaluateQueueBanner, footerStatus, fmtAgo) bez Reacta
// i bez IDB — testowalne w node:test.

import { getSetting, setSetting } from './offlineDB.js';
import { t, tp } from '../shared/i18n.js';

export const LAST_SUCCESS_KEY = 'last_upload_success_at';
/** (a) ZATOR: 5 min bez sukcesu wysyłki przy niepustej kolejce i zasięgu. */
export const STALL_MS = 5 * 60 * 1000;
/** (c) PRÓG: powyżej tylu zdjęć LUB tylu bajtów baner informacyjny. */
export const THRESHOLD_COUNT = 30;
export const THRESHOLD_BYTES = 100 * 1024 * 1024;
/** Odświeżanie czasu względnego w stopce i ocena zatoru w banerze. */
export const HEALTH_TICK_MS = 30_000;

let lastSuccessAt = null;
let loaded = false;
const listeners = new Set();

function emit() {
  for (const fn of listeners) {
    try { fn(lastSuccessAt); } catch { /* słuchacz nie wywraca nadawcy */ }
  }
}

/** Ładuje znacznik ostatniego sukcesu z IDB (raz, przy starcie). Błąd IDB = brak danych. */
export async function initQueueHealth() {
  try {
    const v = await getSetting(LAST_SUCCESS_KEY);
    lastSuccessAt = typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    lastSuccessAt = null;
  }
  loaded = true;
  emit();
  return lastSuccessAt;
}

/** Synchroniczny odczyt (po initQueueHealth; przed nim = null). */
export function getLastSuccessAt() {
  return lastSuccessAt;
}

export function isQueueHealthLoaded() {
  return loaded;
}

/**
 * Wywoływane przez uploadManager po KAŻDEJ udanej wysyłce (201 lub 200 „już mam").
 * Zapis do IDB best-effort — pamięć i tak ustawiona, słuchacze powiadomieni.
 */
export async function recordUploadSuccess(at = Date.now()) {
  lastSuccessAt = at;
  loaded = true;
  try { await setSetting(LAST_SUCCESS_KEY, at); } catch { /* pamięć i tak ustawiona */ }
  emit();
  return at;
}

export function subscribeQueueHealth(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Test hook: reset stanu w pamięci (backing IDB resetuje offlineDB.__useMockBacking). */
export function __resetQueueHealthForTest() {
  lastSuccessAt = null;
  loaded = false;
  listeners.clear();
}

// ─── Czyste funkcje ──────────────────────────────────────────────────────────

/** Odmiana „zdjęcie / zdjęcia / zdjęć" (PL) albo „Foto / Fotos" (DE) — bieżący język (i18n). */
export function plZdjec(n) {
  const a = Math.abs(Math.trunc(Number(n) || 0));
  return tp('queue.photos', a);
}

/** 1 nieudane, 2–4 nieudane, 5+ nieudanych (DE: fehlgeschlagen). */
export function plNieudane(n) {
  const a = Math.abs(Math.trunc(Number(n) || 0));
  return tp('queue.failedN', a);
}

/**
 * Czas względny „X temu": < 60 s → „40 s temu", < 60 min → „3 min temu",
 * < 48 h → „2 godz. temu", dalej „3 dni temu". null gdy brak wartości.
 */
export function fmtAgo(ms) {
  if (ms == null || !Number.isFinite(ms)) return null;
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return t('ago.s', { n: s });
  const m = Math.round(s / 60);
  if (m < 60) return t('ago.min', { n: m });
  const h = Math.round(m / 60);
  if (h < 48) return t('ago.h', { n: h });
  const d = Math.round(h / 24);
  return t('ago.d', { n: d });
}

/**
 * Ocena banera kolejki (UZUPEŁNIENIE 2). null = cisza na ekranie.
 * Zwraca { kind: 'failed' | 'stall' | 'threshold', total, waiting, failed, bytes, ... }.
 * Kolejność: failed (wymaga „Ponów") > stall (wymaga „Wyślij teraz") > próg (informacja).
 *
 * @param {Object} p
 * @param {boolean} p.online       navigator.onLine (offline = osobny baner, nie tu)
 * @param {boolean} p.weak         sygnał „słaby zasięg" z B3 (GET przekroczył limit) — zator wtedy NIE
 * @param {boolean} p.localMode    Tryb lokalny (B9) = osobny stały baner, nie tu
 * @param {Object}  p.stats        getQueueStats(): total/pending/uploading/failed/bytes/oldestWaitingAt
 * @param {number|null} p.lastSuccessAt  znacznik ostatniego SUKCESU wysyłki (ms) lub null
 * @param {number}  p.now
 */
export function evaluateQueueBanner({ online = true, weak = false, localMode = false, stats, lastSuccessAt = null, now = Date.now() } = {}) {
  const s = stats || {};
  const total = Number(s.total) || 0;
  const failed = Number(s.failed) || 0;
  const waiting = (Number(s.pending) || 0) + (Number(s.uploading) || 0);
  const bytes = Number(s.bytes) || 0;
  if (localMode) return null;
  if (total <= 0) return null;

  if (failed > 0) {
    return { kind: 'failed', total, waiting, failed, bytes };
  }

  // (a) ZATOR: zasięg jest, kolejka czeka, a od 5 min zero sukcesów. Punkt odniesienia =
  // ostatni SUKCES; gdy sukcesu nie było wcale (świeże urządzenie) — najstarszy czekający wpis.
  // Nieudane próby NIE przesuwają zegara (zator liczony od sukcesu, nie od próby).
  if (online && !weak && waiting > 0) {
    const oldest = Number(s.oldestWaitingAt) || 0;
    const ref = Math.max(Number(lastSuccessAt) || 0, oldest);
    if (ref > 0 && now - ref >= STALL_MS) {
      return { kind: 'stall', total, waiting, failed, bytes, lastSuccessAt: lastSuccessAt || null, sinceMs: now - ref };
    }
  }

  // (c) PRÓG (informacyjny).
  if (total > THRESHOLD_COUNT || bytes > THRESHOLD_BYTES) {
    return { kind: 'threshold', total, waiting, failed, bytes };
  }
  return null;
}

/**
 * Treść stopki. { kind: 'local' | 'queue' | 'done', text }.
 */
export function footerStatus({ localMode = false, stats, lastSuccessAt = null, now = Date.now() } = {}) {
  const s = stats || {};
  const total = Number(s.total) || 0;
  const failed = Number(s.failed) || 0;
  const ago = lastSuccessAt ? fmtAgo(now - lastSuccessAt) : null;
  if (localMode) {
    return { kind: 'local', text: t('queue.local', { total, photos: plZdjec(total) }) };
  }
  if (total > 0) {
    const f = failed > 0 ? t('queue.failedSuffix', { failed, word: plNieudane(failed) }) : '';
    const tail = ago ? t('queue.lastSent', { ago }) : t('queue.nothingSent');
    return { kind: 'queue', text: t('queue.waiting', { total, photos: plZdjec(total), f, tail }) };
  }
  return { kind: 'done', text: ago ? t('queue.allSentAgo', { ago }) : t('queue.allSent') };
}
